import React, { useState, useEffect, useCallback, useRef } from 'react';
import { getTransactions, createInvoice } from '../services/paymentsService.js';
import { FRONTEND_BASE_URL } from '../services/common.js';

const TransactionsTab = ({ practiceId }) => {
  const invoiceContentRef = useRef(null);
  // Date range states - default to last 30 days
  const [startDate, setStartDate] = useState(() => {
    const start = new Date();
    start.setDate(start.getDate() - 29); // 30 days including today
    const year = start.getFullYear();
    const month = String(start.getMonth() + 1).padStart(2, '0');
    const day = String(start.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  });
  const [endDate, setEndDate] = useState(() => {
    const today = new Date();
    const year = today.getFullYear();
    const month = String(today.getMonth() + 1).padStart(2, '0');
    const day = String(today.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  });
  
  // Date picker UI states
  const [showDatePicker, setShowDatePicker] = useState(false);
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [selectingStart, setSelectingStart] = useState(true);
  const [hoveredDate, setHoveredDate] = useState(null);
  const datePickerRef = useRef(null);
  
  const [transactionsData, setTransactionsData] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [viewMode, setViewMode] = useState('table'); // 'table' or 'byPatient'
  const [expandedPatients, setExpandedPatients] = useState(new Set());
  const [invoicePreview, setInvoicePreview] = useState(null); // { patient, transactions, totals }
  const [creatingInvoice, setCreatingInvoice] = useState(false);
  const [invoiceCreated, setInvoiceCreated] = useState(null); // { success, invoice, error }
  const [selectedProvider, setSelectedProvider] = useState('all'); // 'all' or provider_id
  const [patientSearch, setPatientSearch] = useState(''); // patient name search

  // Click-outside handler for date picker
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (datePickerRef.current && !datePickerRef.current.contains(event.target)) {
        setShowDatePicker(false);
      }
    };

    if (showDatePicker) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showDatePicker]);

  const loadTransactions = useCallback(async () => {
    if (!practiceId) return;
    
    setIsLoading(true);
    setError(null);
    
    try {
      const data = await getTransactions(practiceId, { startDate, endDate });
      
      if (data && data.success) {
        setTransactionsData(data);
      } else {
        setError(data?.error || 'Failed to load transactions');
        setTransactionsData(null);
      }
    } catch (err) {
      console.error('Error loading transactions:', err);
      setError(err.message || 'Failed to load transactions');
      setTransactionsData(null);
    } finally {
      setIsLoading(false);
    }
  }, [practiceId, startDate, endDate]);

  useEffect(() => {
    loadTransactions();
  }, [loadTransactions]);

  // Date range picker helpers
  const formatDateToString = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const formatDateRange = () => {
    if (!startDate || !endDate) {
      return 'Select dates';
    }
    
    const parseDate = (dateString) => {
      const [year, month, day] = dateString.split('-');
      return new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
    };
    
    const startDateObj = parseDate(startDate);
    const endDateObj = parseDate(endDate);
    
    if (startDate === endDate) {
      return startDateObj.toLocaleDateString();
    }
    return `${startDateObj.toLocaleDateString()} - ${endDateObj.toLocaleDateString()}`;
  };

  const openDatePicker = () => {
    setSelectingStart(true);
    setShowDatePicker(true);
  };

  const handleDateClick = (date) => {
    const dateString = formatDateToString(date);
    
    if (selectingStart) {
      setStartDate(dateString);
      setEndDate(dateString);
      setSelectingStart(false);
    } else {
      if (new Date(dateString) >= new Date(startDate)) {
        setEndDate(dateString);
        setShowDatePicker(false);
        setSelectingStart(true);
      } else {
        setStartDate(dateString);
        setEndDate(startDate);
        setShowDatePicker(false);
        setSelectingStart(true);
      }
    }
  };

  const setQuickRange = (days) => {
    const end = new Date();
    const start = new Date();
    start.setDate(end.getDate() - days);
    
    setStartDate(formatDateToString(start));
    setEndDate(formatDateToString(end));
    setShowDatePicker(false);
    setSelectingStart(true);
  };

  const isDateInRange = (date) => {
    if (!startDate || !endDate) return false;
    const dateStr = formatDateToString(date);
    return dateStr >= startDate && dateStr <= endDate;
  };

  const isDateRangeHover = (date) => {
    if (!hoveredDate || !startDate || selectingStart) return false;
    const dateStr = formatDateToString(date);
    const startStr = startDate;
    const hoverStr = formatDateToString(hoveredDate);
    
    if (hoverStr >= startStr) {
      return dateStr >= startStr && dateStr <= hoverStr;
    } else {
      return dateStr >= hoverStr && dateStr <= startStr;
    }
  };

  const isStartDate = (date) => {
    return startDate === formatDateToString(date);
  };

  const isEndDate = (date) => {
    return endDate === formatDateToString(date) && startDate !== endDate;
  };

  const getDaysInMonth = (date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startingDayOfWeek = firstDay.getDay();
    
    const days = [];
    
    for (let i = 0; i < startingDayOfWeek; i++) {
      days.push(null);
    }
    
    for (let day = 1; day <= daysInMonth; day++) {
      days.push(new Date(year, month, day));
    }
    
    return days;
  };

  const navigateMonth = (direction) => {
    setCurrentMonth(prev => {
      const newMonth = new Date(prev);
      newMonth.setMonth(prev.getMonth() + direction);
      return newMonth;
    });
  };

  // Button styles for date picker
  const quickRangeStyle = {
    padding: '6px 10px',
    border: '1px solid #e3e7e4',
    borderRadius: '4px',
    backgroundColor: '#f7f8f7',
    cursor: 'pointer',
    fontSize: '12px',
    color: '#2b302d'
  };

  const navButtonStyle = {
    width: '28px',
    height: '28px',
    border: '1px solid #e3e7e4',
    borderRadius: '4px',
    backgroundColor: 'white',
    cursor: 'pointer',
    fontSize: '16px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center'
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount || 0);
  };

  const handlePrintInvoice = () => {
    if (!invoiceContentRef.current) return;
    
    const content = invoiceContentRef.current.innerHTML;
    
    // Create hidden iframe for printing
    const iframe = document.createElement('iframe');
    iframe.style.position = 'absolute';
    iframe.style.width = '0';
    iframe.style.height = '0';
    iframe.style.border = 'none';
    iframe.style.left = '-9999px';
    document.body.appendChild(iframe);
    
    const doc = iframe.contentWindow.document;
    doc.open();
    doc.write(`
      <!DOCTYPE html>
      <html>
        <head>
          <title>Invoice - ${invoicePreview?.patient?.name || 'Patient'}</title>
          <style>
            body { 
              font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
              margin: 20px;
              color: #333;
            }
            * { box-sizing: border-box; }
          </style>
        </head>
        <body>
          ${content}
        </body>
      </html>
    `);
    doc.close();
    
    iframe.contentWindow.focus();
    iframe.contentWindow.print();
    
    // Remove iframe after print dialog closes
    setTimeout(() => {
      document.body.removeChild(iframe);
    }, 1000);
  };

  const formatDateTimeInTimezone = (isoString, timezone) => {
    if (!isoString) return '-';
    try {
      const date = new Date(isoString);
      return date.toLocaleString('en-US', {
        timeZone: timezone || 'America/New_York',
        month: 'short',
        day: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
        hour12: true
      });
    } catch {
      return '-';
    }
  };

  const practiceTimezone = transactionsData?.timezone || 'America/New_York';

  const getTypeStyle = (type) => {
    switch (type) {
      case 'PROCEDURE':
        return { bg: '#e7f4ec', color: '#1d6b40', label: 'Procedure' };
      case 'PAYMENT':
        return { bg: '#e8f0f7', color: '#1f4e79', label: 'Payment' };
      case 'ADJUSTMENT':
        return { bg: '#fdf3e3', color: '#a8520c', label: 'Adjustment' };
      default:
        return { bg: '#eef0ee', color: '#3c4540', label: type };
    }
  };

  // Aggregate all transactions across dates in range
  const { transactions, totals } = React.useMemo(() => {
    if (!transactionsData?.summary) {
      return { transactions: [], totals: { production: 0, collections: 0, collections_insurance: 0, collections_patient: 0, adjustments: 0 } };
    }
    
    let allTransactions = [];
    let totalProduction = 0;
    let totalCollections = 0;
    let totalCollectionsInsurance = 0;
    let totalCollectionsPatient = 0;
    let totalAdjustments = 0;
    
    // Get dates in the summary and sort them
    const dates = Object.keys(transactionsData.summary).sort();
    
    for (const date of dates) {
      const dayData = transactionsData.summary[date];
      if (dayData?.transactions) {
        // Add date to each transaction for display
        const txWithDate = dayData.transactions.map(tx => ({
          ...tx,
          transaction_date: date
        }));
        allTransactions = allTransactions.concat(txWithDate);
      }
      totalProduction += dayData?.production || 0;
      totalCollections += dayData?.collections || 0;
      totalCollectionsInsurance += dayData?.collections_insurance || 0;
      totalCollectionsPatient += dayData?.collections_patient || 0;
      totalAdjustments += dayData?.adjustments || 0;
    }
    
    // Sort all transactions by transaction_time, then update_time (latest first)
    allTransactions.sort((a, b) => {
      const aTxTime = a.transaction_time || '';
      const bTxTime = b.transaction_time || '';
      if (bTxTime !== aTxTime) {
        return bTxTime.localeCompare(aTxTime);
      }
      const aUpdateTime = a.update_time || '';
      const bUpdateTime = b.update_time || '';
      return bUpdateTime.localeCompare(aUpdateTime);
    });
    
    return {
      transactions: allTransactions,
      totals: {
        production: totalProduction,
        collections: totalCollections,
        collections_insurance: totalCollectionsInsurance,
        collections_patient: totalCollectionsPatient,
        adjustments: totalAdjustments
      }
    };
  }, [transactionsData]);

  // Extract unique providers from transactions
  const uniqueProviders = React.useMemo(() => {
    const providerMap = new Map();
    transactions.forEach(tx => {
      const providerId = tx.provider_id;
      if (providerId && !providerMap.has(providerId)) {
        providerMap.set(providerId, {
          id: providerId,
          name: tx.provider_name || providerId
        });
      }
    });
    // Sort by provider name
    return Array.from(providerMap.values()).sort((a, b) => 
      (a.name || '').localeCompare(b.name || '')
    );
  }, [transactions]);

  // Filter transactions by selected provider and patient search
  const filteredTransactions = React.useMemo(() => {
    let filtered = transactions;
    
    // Filter by provider
    if (selectedProvider !== 'all') {
      filtered = filtered.filter(tx => tx.provider_id === selectedProvider);
    }
    
    // Filter by patient name search
    if (patientSearch.trim()) {
      const searchLower = patientSearch.trim().toLowerCase();
      filtered = filtered.filter(tx => 
        (tx.patient_name || '').toLowerCase().includes(searchLower)
      );
    }
    
    return filtered;
  }, [transactions, selectedProvider, patientSearch]);

  // Aggregate totals over the filtered set (mirrors top-level summary cards)
  const filteredTotals = React.useMemo(() => {
    return filteredTransactions.reduce(
      (acc, tx) => {
        const amount = parseFloat(tx.amount) || 0;
        if (tx.type === 'PROCEDURE') {
          acc.production += amount;
        } else if (tx.type === 'PAYMENT') {
          const abs = Math.abs(amount);
          acc.collections += abs;
          if (tx.transaction_subtype?.toUpperCase().includes('INSURANCE')) {
            acc.collections_insurance += abs;
          } else {
            acc.collections_patient += abs;
          }
        } else if (tx.type === 'ADJUSTMENT') {
          acc.adjustments += amount;
        }
        return acc;
      },
      {
        production: 0,
        collections: 0,
        collections_insurance: 0,
        collections_patient: 0,
        adjustments: 0,
      }
    );
  }, [filteredTransactions]);

  const hasActiveFilters =
    selectedProvider !== 'all' || patientSearch.trim().length > 0;

  // Group transactions by patient (using filtered transactions)
  const transactionsByPatient = React.useMemo(() => {
    const grouped = {};
    filteredTransactions.forEach(tx => {
      const patientId = tx.contact_id || 'unknown';
      if (!grouped[patientId]) {
        grouped[patientId] = {
          patient_name: tx.patient_name || `Patient #${patientId}`,
          contact_id: patientId,
          transactions: [],
          production: 0,
          collections: 0,
          adjustments: 0,
          total: 0,
          latest_transaction_time: null,
          latest_update_time: null
        };
      }
      grouped[patientId].transactions.push(tx);
      const amount = parseFloat(tx.amount) || 0;
      if (tx.type === 'PROCEDURE') {
        grouped[patientId].production += amount;
      } else if (tx.type === 'PAYMENT') {
        grouped[patientId].collections += Math.abs(amount);
      } else if (tx.type === 'ADJUSTMENT') {
        grouped[patientId].adjustments += amount;
      }
      grouped[patientId].total += amount;

      // Track latest transaction_time for this patient
      if (tx.transaction_time && (!grouped[patientId].latest_transaction_time || tx.transaction_time > grouped[patientId].latest_transaction_time)) {
        grouped[patientId].latest_transaction_time = tx.transaction_time;
      }
      // Track latest update_time for this patient
      if (tx.update_time && (!grouped[patientId].latest_update_time || tx.update_time > grouped[patientId].latest_update_time)) {
        grouped[patientId].latest_update_time = tx.update_time;
      }
    });
    // Sort by latest_transaction_time, then latest_update_time (most recent first)
    return Object.values(grouped).sort((a, b) => {
      const aTxTime = a.latest_transaction_time || '';
      const bTxTime = b.latest_transaction_time || '';
      if (bTxTime !== aTxTime) {
        return bTxTime.localeCompare(aTxTime);
      }
      const aUpdateTime = a.latest_update_time || '';
      const bUpdateTime = b.latest_update_time || '';
      return bUpdateTime.localeCompare(aUpdateTime);
    });
  }, [filteredTransactions]);

  const togglePatientExpanded = (patientId) => {
    setExpandedPatients(prev => {
      const next = new Set(prev);
      if (next.has(patientId)) {
        next.delete(patientId);
      } else {
        next.add(patientId);
      }
      return next;
    });
  };

  const renderActiveFilterChips = () => {
    const chips = [];
    if (selectedProvider !== 'all') {
      const p = uniqueProviders.find(x => x.id === selectedProvider);
      chips.push({
        label: `Provider: ${p?.name || selectedProvider}`,
        clear: () => setSelectedProvider('all'),
      });
    }
    if (patientSearch.trim()) {
      chips.push({
        label: `Patient: "${patientSearch}"`,
        clear: () => setPatientSearch(''),
      });
    }
    if (chips.length === 0) return null;
    return (
      <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap', marginTop: '8px' }}>
        {chips.map((chip, i) => (
          <span
            key={i}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '6px',
              padding: '4px 10px',
              background: '#e6efe9',
              color: '#1f5a3d',
              borderRadius: '12px',
              fontSize: '12px',
              fontWeight: 500,
            }}
          >
            {chip.label}
            <button
              onClick={chip.clear}
              style={{
                background: '#1f5a3d',
                color: 'white',
                border: 'none',
                borderRadius: '50%',
                width: '14px',
                height: '14px',
                cursor: 'pointer',
                fontSize: '9px',
                lineHeight: 1,
              }}
            >
              ✕
            </button>
          </span>
        ))}
      </div>
    );
  };

  return (
    <div>
      <div style={{ 
        backgroundColor: 'white', 
        padding: '20px', 
        borderRadius: '8px',
        boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
        marginBottom: '20px'
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
          <h3 style={{ margin: '0', color: '#1f5a3d' }}>Transactions</h3>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            {/* Date Range Picker */}
            <div style={{ position: 'relative' }} ref={datePickerRef}>
              <button
                onClick={openDatePicker}
                style={{
                  padding: '8px 14px',
                  border: '1px solid #e3e7e4',
                  borderRadius: '4px',
                  fontSize: '14px',
                  backgroundColor: 'white',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  minWidth: '200px'
                }}
              >
                <span>📅</span>
                <span style={{ flex: 1, textAlign: 'left' }}>{formatDateRange()}</span>
                <span style={{ fontSize: '10px', color: '#6b716d' }}>▼</span>
              </button>
              
              {showDatePicker && (
                <div style={{
                  position: 'absolute',
                  top: '100%',
                  right: 0,
                  marginTop: '4px',
                  backgroundColor: 'white',
                  border: '1px solid #e3e7e4',
                  borderRadius: '8px',
                  boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                  zIndex: 1000,
                  padding: '16px',
                  width: '320px'
                }}>
                  {/* Header */}
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
                    <h4 style={{ margin: 0, fontSize: '14px', color: '#2b302d' }}>
                      {selectingStart ? 'Select start date' : 'Select end date'}
                    </h4>
                    <button
                      onClick={() => setShowDatePicker(false)}
                      style={{
                        background: 'none',
                        border: 'none',
                        fontSize: '16px',
                        cursor: 'pointer',
                        color: '#6b716d',
                        padding: '4px'
                      }}
                    >
                      ✕
                    </button>
                  </div>
                  
                  {/* Quick Ranges */}
                  <div style={{ display: 'flex', gap: '8px', marginBottom: '16px', flexWrap: 'wrap' }}>
                    <button onClick={() => setQuickRange(0)} style={quickRangeStyle}>Today</button>
                    <button onClick={() => setQuickRange(6)} style={quickRangeStyle}>Last 7 days</button>
                    <button onClick={() => setQuickRange(13)} style={quickRangeStyle}>Last 14 days</button>
                    <button onClick={() => setQuickRange(29)} style={quickRangeStyle}>Last 30 days</button>
                  </div>
                  
                  {/* Calendar */}
                  <div>
                    {/* Month Navigation */}
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
                      <button onClick={() => navigateMonth(-1)} style={navButtonStyle}>‹</button>
                      <span style={{ fontWeight: '500', fontSize: '14px' }}>
                        {currentMonth.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
                      </span>
                      <button onClick={() => navigateMonth(1)} style={navButtonStyle}>›</button>
                    </div>
                    
                    {/* Weekday Headers */}
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: '4px', marginBottom: '8px' }}>
                      {['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'].map(day => (
                        <div key={day} style={{ textAlign: 'center', fontSize: '11px', color: '#8a908c', fontWeight: '500' }}>
                          {day}
                        </div>
                      ))}
                    </div>
                    
                    {/* Calendar Days */}
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: '4px' }}>
                      {getDaysInMonth(currentMonth).map((date, index) => {
                        if (!date) {
                          return <div key={`empty-${index}`} />;
                        }
                        
                        const isToday = formatDateToString(date) === formatDateToString(new Date());
                        const isSelected = isStartDate(date) || isEndDate(date);
                        const isInRange = isDateInRange(date);
                        const isHovered = isDateRangeHover(date);
                        
                        return (
                          <button
                            key={date.getDate()}
                            onClick={() => handleDateClick(date)}
                            onMouseEnter={() => setHoveredDate(date)}
                            onMouseLeave={() => setHoveredDate(null)}
                            style={{
                              width: '36px',
                              height: '36px',
                              border: 'none',
                              borderRadius: isSelected ? '50%' : (isInRange || isHovered) ? '0' : '4px',
                              backgroundColor: isSelected ? '#1f5a3d' : (isInRange || isHovered) ? '#e6efe9' : 'transparent',
                              color: isSelected ? 'white' : isToday ? '#1f5a3d' : '#2b302d',
                              fontWeight: isToday ? '700' : '400',
                              cursor: 'pointer',
                              fontSize: '13px'
                            }}
                          >
                            {date.getDate()}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                  
                  {/* Footer */}
                  <div style={{ marginTop: '12px', paddingTop: '12px', borderTop: '1px solid #eef0ee', fontSize: '12px', color: '#6b716d' }}>
                    {formatDateRange()}
                  </div>
                </div>
              )}
            </div>
            
            <button
              onClick={loadTransactions}
              disabled={isLoading}
              style={{
                backgroundColor: isLoading ? '#cdd3cf' : '#1f5a3d',
                color: 'white',
                border: 'none',
                padding: '8px 16px',
                borderRadius: '4px',
                cursor: isLoading ? 'not-allowed' : 'pointer',
                fontSize: '14px'
              }}
            >
              {isLoading ? 'Loading...' : 'Refresh'}
            </button>
          </div>
        </div>

        {error && (
          <div style={{
            backgroundColor: '#fdecea',
            color: '#912018',
            padding: '12px 16px',
            borderRadius: '4px',
            marginBottom: '20px'
          }}>
            {error}
          </div>
        )}

        {isLoading ? (
          <div style={{ 
            backgroundColor: '#f7f8f7', 
            padding: '40px', 
            borderRadius: '4px',
            textAlign: 'center'
          }}>
            <p style={{ margin: '0', color: '#6b716d' }}>Loading transactions...</p>
          </div>
        ) : transactionsData && !error ? (
          <>
            {/* Summary Cards */}
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(3, 1fr)',
              gap: '16px',
              marginBottom: '24px'
            }}>
              <div style={{
                backgroundColor: '#e7f4ec',
                padding: '20px',
                borderRadius: '8px',
                textAlign: 'center'
              }}>
                <div style={{ fontSize: '14px', color: '#1d6b40', marginBottom: '8px' }}>
                  Production
                </div>
                <div style={{ fontSize: '28px', fontWeight: 'bold', color: '#1d6b40' }}>
                  {formatCurrency(totals.production)}
                </div>
              </div>

              <div style={{
                backgroundColor: '#e8f0f7',
                padding: '20px',
                borderRadius: '8px',
                textAlign: 'center'
              }}>
                <div style={{ fontSize: '14px', color: '#1f4e79', marginBottom: '8px' }}>
                  Collections
                </div>
                <div style={{ fontSize: '28px', fontWeight: 'bold', color: '#1f4e79' }}>
                  {formatCurrency(totals.collections)}
                </div>
                <div style={{ display: 'flex', justifyContent: 'center', gap: '16px', marginTop: '10px', fontSize: '12px' }}>
                  <div>
                    <div style={{ color: '#1f5a3d', fontWeight: '500' }}>Insurance</div>
                    <div style={{ color: '#1f4e79', fontWeight: '600' }}>{formatCurrency(totals.collections_insurance)}</div>
                  </div>
                  <div style={{ borderLeft: '1px solid #c5d8ea', paddingLeft: '16px' }}>
                    <div style={{ color: '#1f5a3d', fontWeight: '500' }}>Patient</div>
                    <div style={{ color: '#1f4e79', fontWeight: '600' }}>{formatCurrency(totals.collections_patient)}</div>
                  </div>
                </div>
              </div>

              <div style={{
                backgroundColor: '#fdf3e3',
                padding: '20px',
                borderRadius: '8px',
                textAlign: 'center'
              }}>
                <div style={{ fontSize: '14px', color: '#a8520c', marginBottom: '8px' }}>
                  Adjustments
                </div>
                <div style={{ fontSize: '28px', fontWeight: 'bold', color: '#a8520c' }}>
                  {formatCurrency(totals.adjustments)}
                </div>
              </div>
            </div>

            {/* View Mode Tabs + Provider Filter */}
            <div style={{ 
              display: 'flex', 
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: '16px',
              borderBottom: '2px solid #eef0ee'
            }}>
              <div style={{ display: 'flex', gap: '0' }}>
                <button
                  onClick={() => setViewMode('table')}
                  style={{
                    padding: '10px 20px',
                    border: 'none',
                    backgroundColor: 'transparent',
                    cursor: 'pointer',
                    fontSize: '14px',
                    fontWeight: viewMode === 'table' ? '600' : '400',
                    color: viewMode === 'table' ? '#1f5a3d' : '#6b716d',
                    borderBottom: viewMode === 'table' ? '2px solid #1f5a3d' : '2px solid transparent',
                    marginBottom: '-2px'
                  }}
                >
                  All Transactions ({filteredTransactions.length})
                </button>
                <button
                  onClick={() => setViewMode('byPatient')}
                  style={{
                    padding: '10px 20px',
                    border: 'none',
                    backgroundColor: 'transparent',
                    cursor: 'pointer',
                    fontSize: '14px',
                    fontWeight: viewMode === 'byPatient' ? '600' : '400',
                    color: viewMode === 'byPatient' ? '#1f5a3d' : '#6b716d',
                    borderBottom: viewMode === 'byPatient' ? '2px solid #1f5a3d' : '2px solid transparent',
                    marginBottom: '-2px'
                  }}
                >
                  By Patient ({transactionsByPatient.length})
                </button>
              </div>

              {/* Filters */}
              <div style={{ 
                display: 'flex', 
                alignItems: 'center',
                gap: '16px',
                marginBottom: '-2px',
                paddingBottom: '8px'
              }}>
                {/* Patient Search */}
                <div style={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
                  <span style={{ position: 'absolute', left: '10px', color: '#8a908c', fontSize: '14px' }}>🔍</span>
                  <input
                    type="text"
                    value={patientSearch}
                    onChange={(e) => setPatientSearch(e.target.value)}
                    placeholder="Search patient..."
                    style={{
                      padding: '6px 10px',
                      paddingLeft: '32px',
                      paddingRight: patientSearch ? '28px' : '10px',
                      border: patientSearch ? '2px solid #1f5a3d' : '1px solid #e3e7e4',
                      borderRadius: '4px',
                      fontSize: '13px',
                      backgroundColor: patientSearch ? '#e6efe9' : 'white',
                      width: '180px',
                      outline: 'none'
                    }}
                  />
                  {patientSearch && (
                    <button
                      onClick={() => setPatientSearch('')}
                      style={{
                        position: 'absolute',
                        right: '6px',
                        top: '50%',
                        transform: 'translateY(-50%)',
                        background: '#1f5a3d',
                        border: 'none',
                        borderRadius: '50%',
                        width: '16px',
                        height: '16px',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: 'white',
                        fontSize: '10px',
                        fontWeight: 'bold',
                        lineHeight: 1
                      }}
                      title="Clear search"
                    >
                      ✕
                    </button>
                  )}
                </div>

                {/* Provider Filter */}
                <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                  <span style={{ fontSize: '13px', color: '#6b716d' }}>Provider:</span>
                  <div style={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
                    <select
                      value={selectedProvider}
                      onChange={(e) => setSelectedProvider(e.target.value)}
                      style={{
                        padding: '6px 10px',
                        paddingRight: selectedProvider !== 'all' ? '28px' : '10px',
                        border: selectedProvider !== 'all' ? '2px solid #1f5a3d' : '1px solid #e3e7e4',
                        borderRadius: '4px',
                        fontSize: '13px',
                        backgroundColor: selectedProvider !== 'all' ? '#e6efe9' : 'white',
                        cursor: 'pointer',
                        minWidth: '160px',
                        fontWeight: selectedProvider !== 'all' ? '500' : 'normal',
                        color: selectedProvider !== 'all' ? '#1f5a3d' : '#2b302d'
                      }}
                    >
                      <option value="all">All Providers</option>
                      {uniqueProviders.map(provider => (
                        <option key={provider.id} value={provider.id}>
                          {provider.name}
                        </option>
                      ))}
                    </select>
                    {selectedProvider !== 'all' && (
                      <button
                        onClick={() => setSelectedProvider('all')}
                        style={{
                          position: 'absolute',
                          right: '6px',
                          top: '50%',
                          transform: 'translateY(-50%)',
                          background: '#1f5a3d',
                          border: 'none',
                          borderRadius: '50%',
                          width: '16px',
                          height: '16px',
                          cursor: 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          color: 'white',
                          fontSize: '10px',
                          fontWeight: 'bold',
                          lineHeight: 1
                        }}
                        title="Clear filter"
                      >
                        ✕
                      </button>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {renderActiveFilterChips()}

            {/* Filtered Totals (only show when filters active) */}
            {hasActiveFilters && (
              <div
                style={{
                  marginTop: '12px',
                  marginBottom: '12px',
                  padding: '10px 14px',
                  background: '#f7f8f7',
                  borderRadius: '6px',
                  fontSize: '13px',
                  color: '#3c4540',
                  display: 'flex',
                  gap: '20px',
                  flexWrap: 'wrap',
                  alignItems: 'center',
                }}
              >
                <span>
                  Filtered totals:&nbsp;
                  <strong>{filteredTransactions.length}</strong> tx
                </span>
                <span>
                  Production:{' '}
                  <strong style={{ color: '#1d6b40' }}>
                    {formatCurrency(filteredTotals.production)}
                  </strong>
                </span>
                <span>
                  Collections:{' '}
                  <strong style={{ color: '#1f4e79' }}>
                    {formatCurrency(filteredTotals.collections)}
                  </strong>
                  <span style={{ color: '#8a908c', marginLeft: '4px', fontSize: '11px' }}>
                    (Ins {formatCurrency(filteredTotals.collections_insurance)} · Pt{' '}
                    {formatCurrency(filteredTotals.collections_patient)})
                  </span>
                </span>
                <span>
                  Adjustments:{' '}
                  <strong style={{ color: '#a8520c' }}>
                    {formatCurrency(filteredTotals.adjustments)}
                  </strong>
                </span>
                <span>
                  Net:{' '}
                  <strong
                    style={{
                      color:
                        filteredTotals.production +
                          filteredTotals.adjustments -
                          filteredTotals.collections >
                        0
                          ? '#b42318'
                          : '#1d6b40',
                    }}
                  >
                    {formatCurrency(
                      filteredTotals.production +
                        filteredTotals.adjustments -
                        filteredTotals.collections
                    )}
                  </strong>
                </span>
              </div>
            )}

            {filteredTransactions.length === 0 ? (
              <div style={{
                backgroundColor: '#f7f8f7',
                padding: '20px', 
                borderRadius: '4px',
                textAlign: 'center'
              }}>
                <p style={{ margin: '0', color: '#6b716d' }}>
                  No transactions found for {formatDateRange()}
                </p>
              </div>
            ) : viewMode === 'byPatient' ? (
              /* By Patient View */
              <div style={{ border: '1px solid #e3e7e4', borderRadius: '8px', overflow: 'hidden' }}>
                {transactionsByPatient.map((patient, pIndex) => {
                  const isExpanded = expandedPatients.has(patient.contact_id);
                  return (
                    <div key={patient.contact_id}>
                      {/* Patient Summary Row */}
                      <div
                        onClick={() => togglePatientExpanded(patient.contact_id)}
                        style={{
                          display: 'grid',
                          gridTemplateColumns: '30px 1.2fr 130px 85px 85px 85px 85px 100px',
                          gap: '8px',
                          padding: '14px 15px',
                          backgroundColor: isExpanded ? '#f3f7fb' : 'white',
                          borderBottom: '1px solid #eef0ee',
                          cursor: 'pointer',
                          alignItems: 'center'
                        }}
                      >
                        <div style={{ color: '#6b716d', fontSize: '12px' }}>
                          {isExpanded ? '▼' : '▶'}
                        </div>
                        <div>
                          <div style={{ fontWeight: '600', color: '#2b302d' }}>
                            {patient.patient_name}
                          </div>
                          <div style={{ fontSize: '12px', color: '#8a908c' }}>
                            {patient.transactions.length} transaction{patient.transactions.length !== 1 ? 's' : ''}
                          </div>
                        </div>
                        <div style={{ color: '#8a908c', fontSize: '11px' }}>
                          <div style={{ color: '#8a908c', fontSize: '10px' }}>Last updated</div>
                          {formatDateTimeInTimezone(patient.latest_update_time, practiceTimezone)}
                        </div>
                        <div style={{ textAlign: 'right' }}>
                          <div style={{ fontSize: '10px', color: '#1d6b40', marginBottom: '2px' }}>Production</div>
                          <div style={{ fontWeight: '600', color: '#1d6b40', fontSize: '13px' }}>
                            {formatCurrency(patient.production)}
                          </div>
                        </div>
                        <div style={{ textAlign: 'right' }}>
                          <div style={{ fontSize: '10px', color: '#1f4e79', marginBottom: '2px' }}>Collections</div>
                          <div style={{ fontWeight: '600', color: '#1f4e79', fontSize: '13px' }}>
                            {formatCurrency(patient.collections)}
                          </div>
                        </div>
                        <div style={{ textAlign: 'right' }}>
                          <div style={{ fontSize: '10px', color: '#a8520c', marginBottom: '2px' }}>Adjustments</div>
                          <div style={{ fontWeight: '600', color: '#a8520c', fontSize: '13px' }}>
                            {formatCurrency(patient.adjustments)}
                          </div>
                        </div>
                        <div style={{ textAlign: 'right' }}>
                          <div style={{ fontSize: '10px', color: '#2b302d', marginBottom: '2px' }}>Balance</div>
                          <div style={{ fontWeight: '700', color: patient.total > 0 ? '#b42318' : '#1d6b40', fontSize: '14px' }}>
                            {formatCurrency(patient.total)}
                          </div>
                        </div>
                        <div style={{ textAlign: 'center' }}>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              // Get phone/email from first transaction (all transactions for same patient have same contact info)
                              const firstTx = patient.transactions[0] || {};
                              setInvoicePreview({
                                patient: patient.patient_name,
                                contact_id: patient.contact_id,
                                patient_phone: firstTx.patient_phone || '',
                                patient_email: firstTx.patient_email || '',
                                transactions: patient.transactions,
                                production: patient.production,
                                collections: patient.collections,
                                adjustments: patient.adjustments,
                                balance: patient.total
                              });
                            }}
                            style={{
                              backgroundColor: '#1f5a3d',
                              color: 'white',
                              border: 'none',
                              padding: '6px 12px',
                              borderRadius: '4px',
                              cursor: 'pointer',
                              fontSize: '11px',
                              fontWeight: '500',
                              whiteSpace: 'nowrap'
                            }}
                          >
                            Invoice
                          </button>
                        </div>
                      </div>
                      
                      {/* Expanded Transaction Rows */}
                      {isExpanded && (
                        <div style={{ backgroundColor: '#f7f8f7', borderBottom: '1px solid #e3e7e4' }}>
                          {patient.transactions.map((tx, txIndex) => {
                            const typeStyle = getTypeStyle(tx.type);
                            return (
                              <div
                                key={tx.remote_id || txIndex}
                                style={{
                                  display: 'grid',
                                  gridTemplateColumns: '30px 70px 70px 100px 70px 1fr 100px 85px',
                                  gap: '8px',
                                  padding: '10px 15px',
                                  borderBottom: txIndex < patient.transactions.length - 1 ? '1px solid #eef0ee' : 'none',
                                  fontSize: '13px',
                                  alignItems: 'center'
                                }}
                              >
                                <div></div>
                                <div style={{ color: '#6b716d', fontSize: '11px' }}>
                                  {tx.transaction_time ? new Date(tx.transaction_time).toLocaleDateString('en-US', { 
                                    timeZone: practiceTimezone,
                                    month: 'short', 
                                    day: 'numeric' 
                                  }) : '-'}
                                </div>
                                <div>
                                  <span style={{
                                    padding: '3px 6px',
                                    borderRadius: '10px',
                                    fontSize: '10px',
                                    fontWeight: '500',
                                    backgroundColor: typeStyle.bg,
                                    color: typeStyle.color
                                  }}>
                                    {typeStyle.label}
                                  </span>
                                </div>
                                <div style={{ color: '#6b716d', fontSize: '11px' }}>
                                  {tx.provider_name || tx.provider_id || '-'}
                                </div>
                                <div style={{ fontFamily: 'monospace', color: '#6b716d', fontSize: '11px' }}>
                                  {tx.procedure_code || '-'}
                                </div>
                                <div style={{ color: '#4a524d', fontSize: '11px' }}>
                                {tx.procedure_description || tx.transaction_subtype?.replace(/_/g, ' ') || tx.note || '-'}
                                </div>
                                <div style={{ color: '#8a908c', fontSize: '10px' }}>
                                  {formatDateTimeInTimezone(tx.update_time, practiceTimezone)}
                                </div>
                                <div style={{
                                  textAlign: 'right',
                                  fontWeight: '600',
                                  fontSize: '13px',
                                  color: tx.type === 'PAYMENT' ? '#1f4e79' : 
                                         tx.type === 'ADJUSTMENT' ? '#a8520c' : '#1d6b40'
                                }}>
                                  {formatCurrency(tx.amount)}
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            ) : (
              /* Table View */
              <div style={{ border: '1px solid #e3e7e4', borderRadius: '8px', overflow: 'hidden' }}>
                {/* Table Header */}
                <div style={{
                  display: 'grid',
                  gridTemplateColumns: startDate !== endDate
                    ? '80px 90px 1fr 120px 80px 1fr 110px 90px'
                    : '90px 1.1fr 130px 90px 1fr 110px 90px',
                  gap: '10px',
                  padding: '12px 15px',
                  backgroundColor: '#f7f8f7',
                  fontWeight: 'bold',
                  fontSize: '13px',
                  color: '#3c4540',
                  borderBottom: '1px solid #e3e7e4'
                }}>
                  {startDate !== endDate && <div>Date</div>}
                  <div>Type</div>
                  <div>Patient</div>
                  <div>Provider</div>
                  <div>Code</div>
                  <div>Description</div>
                  <div>Updated</div>
                  <div style={{ textAlign: 'right' }}>Amount</div>
                </div>
                
                {/* Transaction Rows */}
                {filteredTransactions.map((tx, index) => {
                  const typeStyle = getTypeStyle(tx.type);
                  return (
                    <div
                    key={tx.remote_id || index}
                      style={{
                        display: 'grid',
                        gridTemplateColumns: startDate !== endDate
                          ? '80px 90px 1fr 120px 80px 1fr 110px 90px'
                          : '90px 1.1fr 130px 90px 1fr 110px 90px',
                        gap: '10px',
                        padding: '12px 15px',
                        borderBottom: index < filteredTransactions.length - 1 ? '1px solid #eef0ee' : 'none',
                        backgroundColor: 'white',
                        fontSize: '14px',
                        alignItems: 'center'
                      }}
                    >
                      {startDate !== endDate && (
                        <div style={{ color: '#6b716d', fontSize: '12px' }}>
                          {tx.transaction_date ? new Date(tx.transaction_date + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : '-'}
                        </div>
                      )}
                      <div>
                        <span style={{
                          padding: '4px 8px',
                          borderRadius: '12px',
                          fontSize: '11px',
                          fontWeight: '500',
                          backgroundColor: typeStyle.bg,
                          color: typeStyle.color
                        }}>
                          {typeStyle.label}
                        </span>
                      </div>
                      <div style={{ color: '#2b302d', fontWeight: '500' }}>
                        {tx.patient_name || '-'}
                      </div>
                      <div style={{ color: '#6b716d', fontSize: '13px' }}>
                        {tx.provider_name || tx.provider_id || '-'}
                      </div>
                      <div style={{ fontFamily: 'monospace', color: '#6b716d', fontSize: '13px' }}>
                        {tx.procedure_code || '-'}
                      </div>
                      <div style={{ color: '#4a524d' }}>
                      {tx.procedure_description || tx.transaction_subtype?.replace(/_/g, ' ') || tx.note || '-'}
                      </div>
                        <div style={{ color: '#8a908c', fontSize: '11px' }}>
                          {formatDateTimeInTimezone(tx.update_time, practiceTimezone)}
                        </div>
                      <div style={{
                        textAlign: 'right',
                        fontWeight: '600',
                        color: tx.type === 'PAYMENT' ? '#1f4e79' : 
                               tx.type === 'ADJUSTMENT' ? '#a8520c' : '#1d6b40'
                      }}>
                        {formatCurrency(tx.amount)}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </>
        ) : !error && (
          <div style={{ 
            backgroundColor: '#f7f8f7', 
            padding: '40px', 
            borderRadius: '4px',
            textAlign: 'center'
          }}>
            <p style={{ margin: '0', color: '#6b716d' }}>
              Select a date to view transactions
            </p>
          </div>
        )}
      </div>

      {/* Invoice Preview Modal */}
      {invoicePreview && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            backgroundColor: 'white',
            borderRadius: '8px',
            maxWidth: '800px',
            width: '95%',
            maxHeight: '90vh',
            overflow: 'auto',
            boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)'
          }}>
            {/* Printable Invoice Content */}
            <div ref={invoiceContentRef}>
            {/* Invoice Header */}
            <div style={{ 
              padding: '24px 24px 16px', 
              borderBottom: '2px solid #1f5a3d',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'flex-start'
            }}>
              <div>
                <h2 style={{ margin: '0 0 8px 0', color: '#1f5a3d', fontSize: '24px' }}>INVOICE</h2>
                <div style={{ color: '#6b716d', fontSize: '13px' }}>
                  Date Range: {formatDateRange()}
                </div>
              </div>
              <button
                onClick={() => setInvoicePreview(null)}
                style={{
                  background: 'none',
                  border: 'none',
                  fontSize: '24px',
                  cursor: 'pointer',
                  color: '#6b716d',
                  padding: '0'
                }}
              >
                ✕
              </button>
            </div>

            {/* Patient Info */}
            <div style={{ padding: '20px 24px', backgroundColor: '#f7f8f7' }}>
              <div style={{ fontSize: '12px', color: '#6b716d', marginBottom: '4px' }}>BILL TO</div>
              <div style={{ fontSize: '18px', fontWeight: '600', color: '#2b302d' }}>
                {invoicePreview.patient}
              </div>
              <div style={{ fontSize: '12px', color: '#8a908c', marginTop: '4px' }}>
                Patient ID: {invoicePreview.contact_id}
              </div>
            </div>

            {/* Line Items */}
            <div style={{ padding: '20px 24px' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
                <thead>
                  <tr style={{ borderBottom: '2px solid #e3e7e4' }}>
                    <th style={{ textAlign: 'left', padding: '10px 8px', color: '#6b716d', fontWeight: '600' }}>Date</th>
                    <th style={{ textAlign: 'left', padding: '10px 8px', color: '#6b716d', fontWeight: '600' }}>Type</th>
                    <th style={{ textAlign: 'left', padding: '10px 8px', color: '#6b716d', fontWeight: '600' }}>Code</th>
                    <th style={{ textAlign: 'left', padding: '10px 8px', color: '#6b716d', fontWeight: '600' }}>Description</th>
                    <th style={{ textAlign: 'right', padding: '10px 8px', color: '#6b716d', fontWeight: '600' }}>Amount</th>
                  </tr>
                </thead>
                <tbody>
                  {invoicePreview.transactions
                    .filter(tx => tx.type === 'PROCEDURE')
                    .map((tx, idx) => (
                      <tr key={tx.remote_id || idx} style={{ borderBottom: '1px solid #eef0ee' }}>
                        <td style={{ padding: '10px 8px', color: '#6b716d' }}>
                          {tx.transaction_time ? new Date(tx.transaction_time).toLocaleDateString('en-US', { 
                            timeZone: practiceTimezone,
                            month: 'short', 
                            day: 'numeric' 
                          }) : '-'}
                        </td>
                        <td style={{ padding: '10px 8px' }}>
                          <span style={{ 
                            padding: '2px 6px', 
                            backgroundColor: '#e7f4ec', 
                            color: '#1d6b40', 
                            borderRadius: '4px', 
                            fontSize: '11px' 
                          }}>
                            Procedure
                          </span>
                        </td>
                        <td style={{ padding: '10px 8px', fontFamily: 'monospace', color: '#6b716d' }}>
                          {tx.procedure_code || '-'}
                        </td>
                        <td style={{ padding: '10px 8px', color: '#2b302d' }}>
                          {tx.procedure_description || tx.note || '-'}
                        </td>
                        <td style={{ padding: '10px 8px', textAlign: 'right', fontWeight: '500', color: '#1d6b40' }}>
                          {formatCurrency(tx.amount)}
                        </td>
                      </tr>
                    ))}
                </tbody>
              </table>

              {/* Subtotal */}
              {(() => {
                // Calculate insurance vs patient payments
                const insurancePayments = invoicePreview.transactions
                  .filter(tx => tx.type === 'PAYMENT' && tx.transaction_subtype?.toUpperCase().includes('INSURANCE'))
                  .reduce((sum, tx) => sum + Math.abs(parseFloat(tx.amount) || 0), 0);
                const patientPayments = invoicePreview.transactions
                  .filter(tx => tx.type === 'PAYMENT' && !tx.transaction_subtype?.toUpperCase().includes('INSURANCE'))
                  .reduce((sum, tx) => sum + Math.abs(parseFloat(tx.amount) || 0), 0);
                
                return (
                  <div style={{ 
                    marginTop: '16px', 
                    paddingTop: '16px', 
                    borderTop: '2px solid #e3e7e4',
                    display: 'flex',
                    justifyContent: 'flex-end'
                  }}>
                    <div style={{ width: '280px' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 0' }}>
                        <span style={{ color: '#6b716d' }}>Subtotal (Procedures):</span>
                        <span style={{ fontWeight: '500' }}>{formatCurrency(invoicePreview.production)}</span>
                      </div>
                      <div style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 0' }}>
                        <span style={{ color: '#6b716d' }}>Adjustments:</span>
                        <span style={{ fontWeight: '500', color: invoicePreview.adjustments < 0 ? '#1d6b40' : '#a8520c' }}>
                          {formatCurrency(invoicePreview.adjustments)}
                        </span>
                      </div>
                      <div style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 0' }}>
                        <span style={{ color: '#6b716d' }}>Insurance Payments:</span>
                        <span style={{ fontWeight: '500', color: '#1f5a3d' }}>
                          -{formatCurrency(insurancePayments)}
                        </span>
                      </div>
                      <div style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 0' }}>
                        <span style={{ color: '#6b716d' }}>Patient Payments:</span>
                        <span style={{ fontWeight: '500', color: '#1f4e79' }}>
                          -{formatCurrency(patientPayments)}
                        </span>
                      </div>
                      <div style={{ 
                        display: 'flex', 
                        justifyContent: 'space-between', 
                        padding: '12px 0 6px',
                        borderTop: '2px solid #2b302d',
                        marginTop: '8px'
                      }}>
                        <span style={{ fontWeight: '700', fontSize: '16px' }}>Balance Due:</span>
                        <span style={{ 
                          fontWeight: '700', 
                          fontSize: '18px',
                          color: invoicePreview.balance > 0 ? '#b42318' : '#1d6b40'
                        }}>
                          {formatCurrency(invoicePreview.balance)}
                        </span>
                      </div>
                    </div>
                  </div>
                );
              })()}

              {/* Payments Section - grouped by Insurance vs Patient */}
              {invoicePreview.transactions.filter(tx => tx.type === 'PAYMENT').length > 0 && (
                <div style={{ marginTop: '24px' }}>
                  {/* Insurance Payments */}
                  {invoicePreview.transactions.filter(tx => tx.type === 'PAYMENT' && tx.transaction_subtype?.toUpperCase().includes('INSURANCE')).length > 0 && (
                    <>
                      <h4 style={{ margin: '0 0 12px', color: '#1f5a3d', fontSize: '13px', fontWeight: '600' }}>
                        INSURANCE PAYMENTS
                      </h4>
                      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12px', marginBottom: '20px' }}>
                        <tbody>
                          {invoicePreview.transactions
                            .filter(tx => tx.type === 'PAYMENT' && tx.transaction_subtype?.toUpperCase().includes('INSURANCE'))
                            .map((tx, idx) => (
                              <tr key={tx.remote_id || idx} style={{ borderBottom: '1px solid #eef0ee' }}>
                                <td style={{ padding: '8px', color: '#6b716d', width: '100px' }}>
                                  {tx.transaction_time ? new Date(tx.transaction_time).toLocaleDateString('en-US', { 
                                    timeZone: practiceTimezone,
                                    month: 'short', 
                                    day: 'numeric' 
                                  }) : '-'}
                                </td>
                                <td style={{ padding: '8px' }}>
                                  <span style={{ 
                                    padding: '2px 6px', 
                                    backgroundColor: '#e8f0f7', 
                                    color: '#1f5a3d', 
                                    borderRadius: '4px', 
                                    fontSize: '10px' 
                                  }}>
                                    Insurance
                                  </span>
                                </td>
                                <td style={{ padding: '8px', color: '#6b716d' }}>
                                  {tx.note || tx.transaction_subtype?.replace(/_/g, ' ') || '-'}
                                </td>
                                <td style={{ padding: '8px', textAlign: 'right', fontWeight: '500', color: '#1f5a3d' }}>
                                  {formatCurrency(Math.abs(tx.amount))}
                                </td>
                              </tr>
                            ))}
                        </tbody>
                      </table>
                    </>
                  )}

                  {/* Patient Payments */}
                  {invoicePreview.transactions.filter(tx => tx.type === 'PAYMENT' && !tx.transaction_subtype?.toUpperCase().includes('INSURANCE')).length > 0 && (
                    <>
                      <h4 style={{ margin: '0 0 12px', color: '#1f4e79', fontSize: '13px', fontWeight: '600' }}>
                        PATIENT PAYMENTS
                      </h4>
                      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12px' }}>
                        <tbody>
                          {invoicePreview.transactions
                            .filter(tx => tx.type === 'PAYMENT' && !tx.transaction_subtype?.toUpperCase().includes('INSURANCE'))
                            .map((tx, idx) => (
                              <tr key={tx.remote_id || idx} style={{ borderBottom: '1px solid #eef0ee' }}>
                                <td style={{ padding: '8px', color: '#6b716d', width: '100px' }}>
                                  {tx.transaction_time ? new Date(tx.transaction_time).toLocaleDateString('en-US', { 
                                    timeZone: practiceTimezone,
                                    month: 'short', 
                                    day: 'numeric' 
                                  }) : '-'}
                                </td>
                                <td style={{ padding: '8px' }}>
                                  <span style={{ 
                                    padding: '2px 6px', 
                                    backgroundColor: '#e8f0f7', 
                                    color: '#1f4e79', 
                                    borderRadius: '4px', 
                                    fontSize: '10px' 
                                  }}>
                                    Patient
                                  </span>
                                </td>
                                <td style={{ padding: '8px', color: '#6b716d' }}>
                                  {tx.note || tx.transaction_subtype?.replace(/_/g, ' ') || '-'}
                                </td>
                                <td style={{ padding: '8px', textAlign: 'right', fontWeight: '500', color: '#1f4e79' }}>
                                  {formatCurrency(Math.abs(tx.amount))}
                                </td>
                              </tr>
                            ))}
                        </tbody>
                      </table>
                    </>
                  )}
                </div>
              )}
            </div>
            </div>{/* End of printable invoice content */}

            {/* Invoice Created Success Message */}
            {invoiceCreated && (
              <div style={{
                padding: '12px 24px',
                backgroundColor: invoiceCreated.success ? '#e7f4ec' : '#fdecea',
                borderTop: '1px solid',
                borderColor: invoiceCreated.success ? '#c6e3d0' : '#f5c6c1',
                color: invoiceCreated.success ? '#1d6b40' : '#912018'
              }}>
                {invoiceCreated.success ? (
                  <div>
                    <strong>Invoice Created!</strong>
                    <span style={{ marginLeft: '8px' }}>
                      ID: {invoiceCreated.invoice?.id}
                    </span>
                    {invoiceCreated.invoice?.publicHash && (
                      <button
                        onClick={() => {
                          const url = `${FRONTEND_BASE_URL}/invoice/${invoiceCreated.invoice.publicHash}`;
                          navigator.clipboard.writeText(url);
                        }}
                        style={{
                          marginLeft: '12px',
                          backgroundColor: '#1d6b40',
                          color: 'white',
                          border: 'none',
                          padding: '4px 10px',
                          borderRadius: '4px',
                          cursor: 'pointer',
                          fontSize: '12px'
                        }}
                      >
                        Copy Payment Link
                      </button>
                    )}
                  </div>
                ) : (
                  <span><strong>Error:</strong> {invoiceCreated.error}</span>
                )}
              </div>
            )}

            {/* Footer Actions */}
            <div style={{ 
              padding: '16px 24px', 
              borderTop: '1px solid #eef0ee', 
              display: 'flex', 
              justifyContent: 'flex-end',
              gap: '12px',
              backgroundColor: '#f7f8f7'
            }}>
              <button
                onClick={handlePrintInvoice}
                style={{
                  backgroundColor: 'white',
                  color: '#2b302d',
                  border: '1px solid #e3e7e4',
                  padding: '10px 20px',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: '500'
                }}
              >
                Print
              </button>
              <button
                onClick={async () => {
                  if (creatingInvoice) return;
                  
                  setCreatingInvoice(true);
                  setInvoiceCreated(null);
                  
                  try {
                    // Build line items from ALL transactions with dates
                    const lineItems = [];
                    
                    // Calculate totals for summary
                    let subtotalProcedures = 0;
                    let totalAdjustments = 0;
                    let totalInsurancePayments = 0;
                    let totalPatientPayments = 0;
                    
                    // Add PROCEDURES (positive charges)
                    invoicePreview.transactions
                      .filter(tx => tx.type === 'PROCEDURE')
                      .forEach(tx => {
                        const amount = parseFloat(tx.amount) || 0;
                        subtotalProcedures += amount;
                        lineItems.push({
                          description: tx.procedure_description || tx.procedure_code || 'Procedure',
                          quantity: 1,
                          price: amount,
                          code: tx.procedure_code || '',
                          type: 'PROCEDURE',
                          date: tx.transaction_time || null
                        });
                      });
                    
                    // Add ADJUSTMENTS (can be positive or negative)
                    invoicePreview.transactions
                      .filter(tx => tx.type === 'ADJUSTMENT')
                      .forEach(tx => {
                        const amount = parseFloat(tx.amount) || 0;
                        totalAdjustments += amount;
                        lineItems.push({
                          description: tx.note || tx.transaction_subtype?.replace(/_/g, ' ') || 'Adjustment',
                          quantity: 1,
                          price: amount,
                          code: '',
                          type: 'ADJUSTMENT',
                          date: tx.transaction_time || null
                        });
                      });
                    
                    // Add PAYMENTS as negative (credits)
                    invoicePreview.transactions
                      .filter(tx => tx.type === 'PAYMENT')
                      .forEach(tx => {
                        const amount = Math.abs(parseFloat(tx.amount) || 0);
                        const isInsurance = tx.transaction_subtype?.toUpperCase().includes('INSURANCE');
                        if (isInsurance) {
                          totalInsurancePayments += amount;
                        } else {
                          totalPatientPayments += amount;
                        }
                        lineItems.push({
                          description: tx.note || tx.transaction_subtype?.replace(/_/g, ' ') || 'Payment',
                          quantity: 1,
                          price: -amount,
                          code: '',
                          type: 'PAYMENT',
                          isInsurance: isInsurance,
                          date: tx.transaction_time || null
                        });
                      });
                    
                    // Calculate due date (7 days from now)
                    const dueDate = new Date();
                    dueDate.setDate(dueDate.getDate() + 7);
                    const dueDateStr = dueDate.toISOString().split('T')[0];
                    
                    const invoiceData = {
                      patientId: invoicePreview.contact_id,
                      patientName: invoicePreview.patient,
                      patientPhone: invoicePreview.patient_phone || '',
                      patientEmail: invoicePreview.patient_email || '',
                      lineItems: lineItems,
                      total: invoicePreview.balance > 0 ? invoicePreview.balance : 0,
                      dueDate: dueDateStr,
                      // Summary totals for display
                      summary: {
                        subtotalProcedures,
                        totalAdjustments,
                        totalInsurancePayments,
                        totalPatientPayments,
                        balanceDue: invoicePreview.balance
                      },
                      dateRange: {
                        start: startDate,
                        end: endDate
                      }
                    };
                    
                    const result = await createInvoice(practiceId, invoiceData);
                    
                    if (result.success) {
                      setInvoiceCreated({ success: true, invoice: result.invoice });
                    } else {
                      setInvoiceCreated({ success: false, error: result.error || 'Failed to create invoice' });
                    }
                  } catch (err) {
                    console.error('Error creating invoice:', err);
                    setInvoiceCreated({ success: false, error: err.message || 'Failed to create invoice' });
                  } finally {
                    setCreatingInvoice(false);
                  }
                }}
                disabled={creatingInvoice || invoicePreview.balance <= 0}
                style={{
                  backgroundColor: creatingInvoice ? '#6b716d' : (invoicePreview.balance <= 0 ? '#cdd3cf' : '#1d6b40'),
                  color: 'white',
                  border: 'none',
                  padding: '10px 20px',
                  borderRadius: '4px',
                  cursor: creatingInvoice || invoicePreview.balance <= 0 ? 'not-allowed' : 'pointer',
                  fontSize: '14px',
                  fontWeight: '500'
                }}
                title={invoicePreview.balance <= 0 ? 'No balance to invoice' : 'Create payment invoice'}
              >
                {creatingInvoice ? 'Creating...' : 'Create Invoice'}
              </button>
              <button
                onClick={() => {
                  setInvoicePreview(null);
                  setInvoiceCreated(null);
                }}
                style={{
                  backgroundColor: '#1f5a3d',
                  color: 'white',
                  border: 'none',
                  padding: '10px 20px',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: '500'
                }}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TransactionsTab;
